import { installSourceContextAtom } from '@/atoms/install-source';
import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { useRegion } from '@/helpers/getRegion';
import { useCurrentEhrLaunchProfile } from '@/hooks/integrations/useCurrentEhrLaunchProfile';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getAppType, getPlatform } from '@/lib/analytics/legacy-sender-context';
import { setSnowplowClientContext } from '@/lib/snowplow';
import { setFlagsmithFlagUserProperties } from '@/providers/amplitude-flagsmith-flag-properties';
import { type RootState } from '@/store';
import { resolveUpdateChannel } from '@/tauri/updater/channel';
import { isTauri } from '@/tauri/utils';
import {
  getDesktopInstallerType,
  getDistributionChannel,
} from '@/utils/appConfiguration';
import { getDesktopBuildId } from '@/utils/appVersion';
import { getDesktopOsVersion } from '@/utils/desktopOsInfo';
import { logger } from '@/utils/logger/logger';
import { setDatadogRegion, setDatadogUpdateChannel } from '@/utils/tracing';
import { FEATURE_FLAG_DOMAIN, setGlobalFlagContext } from '@heidi/runtime/feature-flags';
import { NON_CANONICAL_REGION, toValidRegion } from '@heidi/utils';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useWorkspaceId } from '@heidi/workspace/flags';
import { type FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import {
  type EvaluationContext,
  OpenFeature,
  ProviderEvents,
} from '@openfeature/react-sdk';
import { useAtomValue } from 'jotai';
import { isEqual } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { browserName, deviceType, osName } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { useOfflineFlagCacheSync } from './offline-flag-cache-sync';

/**
 * Syncs user and team data to OpenFeature context for feature flag targeting.
 *
 * Traits here must stay flat (string | number | boolean | null) because
 * Flagsmith does not support nested trait objects.
 */
export function OpenFeatureContextSync() {
  const { data: userData } = useQueryUser();
  const teamInfo = useCombinedQueriesTeamGeneralInfo();
  // Read the org from joined orgs, which load for every member.
  // organizationDetails only loads for org admins and template managers.
  const { joinedOrganizationData } = useCombinedOrganizationInfo();
  const joinedOrg = joinedOrganizationData?.organizations?.at(0);
  // Active workspace (available to every member). Dual-written next to org_id
  // so Flagsmith segments can migrate off org_* without a FE gap.
  const workspaceId = useWorkspaceId();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const {
    integrationEhrProvider,
    integrationOnboardingProvider,
    integrationOnboardingCreatedAt,
    integrationOnboardingUpdatedAt,
  } = useIntegrationEhrInfo();
  const userEhrLaunchProfile = useCurrentEhrLaunchProfile();
  const previousContextRef = useRef<EvaluationContext | null>(null);
  const region = useRegion();
  // APP-6116 (§5.11): the AUTHENTICATED region (kindeProperties.region, the
  // regulatory key) drives the desktop `update_channel` trait — distinct from
  // `useRegion()` above, which fails open to AU / honours ?region= (C10) and
  // stays the source for the legacy `region` trait. `update_channel` is
  // desktop-only (web has no updater channel); `build_id` is the baked CI SHA.
  const { data: kindeProperties } = useQueryKindeProperties();
  const isDesktop = getPlatform() === 'desktop';
  const updateChannel = isDesktop
    ? (resolveUpdateChannel(kindeProperties?.region) ?? null)
    : null;
  const buildId = getDesktopBuildId() ?? null;
  // Authenticated (regulatory) region, distinct from the fail-open `region` above.
  const authenticatedRegion = kindeProperties?.region ?? null;
  // PRODA-174: native host OS version from the Tauri os_info session cache;
  // null on web and until the desktop IPC resolves. Read from the module getter
  // (NOT reactive, unlike the Redux-backed desktop_version / cpu_arch): a
  // boot-race null is only re-pushed if some OTHER dependency (region / team /
  // billing) churns after the IPC resolves — best-effort, not guaranteed. In
  // practice initDesktopOsInfo() is kicked off at boot and the local IPC
  // resolves well before the post-login identify fires, so the first push
  // almost always already carries the value. Distinct from `desktop_version`
  // (the Heidi app semver).
  const desktopOsVersion = getDesktopOsVersion() ?? null;
  const installSourceContext = useAtomValue(installSourceContextAtom);
  const offlineConnectivity = useAtomValue(offlineConnectivityStateAtom);
  const isDesktopOnlineReady =
    !isTauri() || offlineConnectivity === 'online_ready_to_sync';

  // The offline flag-decision cache: recovery, the per-settle write, and the
  // withdrawal that retires a replay once the provider answers again.
  useOfflineFlagCacheSync({
    userRefId: userData?.user_ref_id,
    isDesktopOnlineReady,
  });

  const authProvider = useSelector((state: RootState) => state.auth.authProvider);
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const isSettledAnonymous = !isAuthLoading && !isAuthenticated;

  // Desktop-specific state
  const desktopVersion = useSelector(
    (state: RootState) => state.tauriUpdateModel?.current_version
  );
  const cpuArch = useSelector((state: RootState) => state.tauriUpdateModel?.cpu_arch);

  // Get current member from team details
  const currentMember = teamInfo.teamDetails?.team.team_members?.find(
    (member) => member.kinde_user_id === userData?.kinde_user_id
  );

  const ehrProvider =
    integrationEhrProvider ?? userEhrLaunchProfile?.ehr_provider ?? null;
  const hasIntegration = Boolean(integrationEhrProvider || userEhrLaunchProfile);

  useEffect(() => {
    // Identify whenever the user is known, regardless of offline connectivity.
    // Gating setContext on `isDesktopOnlineReady` would withhold ALL flag
    // targeting on desktop — the connectivity atom stays 'unknown' until a later
    // phase wires it — not just the offline-recording flag. The offline
    // suppression is scoped to `useOfflineFlagCacheSync` (mounted above), not
    // here.
    if (!userData?.user_ref_id) return;

    const context: EvaluationContext = {
      targetingKey: userData.user_ref_id,
      traits: {
        // Note: Not all Openfeature providers support nested trait objects so we just use a flat structure
        // Flagsmith *doesn't* support this (https://docs.flagsmith.com/flagsmith-concepts/identities#trait-value-data-types)

        // Kinde user id persisted as a trait so it flows through the Flagsmith
        // identity webhook, letting downstream consumers resolve the identity
        // (keyed by user_ref_id) to a canonical kinde_user_id without a lookup.
        kinde_user_id: userData?.kinde_user_id ?? null,
        email: userData?.email ?? null,
        country: userData?.country ?? null,
        region: region,
        given_name: userData?.first_name ?? null,
        family_name: userData?.last_name ?? null,
        has_phone_number: !!userData?.phone_number,
        user_team_id: teamInfo.teamOrgId ?? null,
        user_team_name: teamInfo.teamDetails?.team.name ?? null,
        user_team_role: teamInfo.userRoleInTeam ?? null,
        user_team_plan: currentMember?.tier ?? null,
        user_team_mfa_enabled: !!teamInfo.teamDetails?.team.mfa_info?.method,
        user_joined_team_date: currentMember?.created_at ?? null,
        user_tier: userData?.tier ?? null,
        is_managed: !!userData?.is_managed,

        team_tier: teamInfo.teamDetails?.team.tier ?? null,
        effective_tier:
          billingInfo?.third_party_license?.latest_tier ?? billingInfo?.tier ?? 'unknown',
        mfa_enabled: !!userData?.user_mfa_info?.is_mfa_enabled,
        opted_in_experimental: !!userData?.session_settings?.opted_in_experimental,
        display_language: userData?.language_time_settings?.display_language ?? null,

        // Organization Traits
        org_id: joinedOrg?.org_id ?? null,
        org_name: joinedOrg?.org_name ?? null,
        // Workspace Traits — keep org_* until Flagsmith segments cut over.
        workspace_id: workspaceId ?? null,

        // Platform & Device Traits
        app_type: getAppType(),
        // Lowercase `platform` matches the Amplitude SDK convention used by
        // iOS/Android native ('ios'/'android'/'web'/'desktop'). Co-exists
        // with `app_type` (PascalCase) for backward-compat with existing
        // Flagsmith targeting rules — APP-4934.
        platform: getPlatform(),
        device_type: deviceType ?? null,
        os_name: osName ?? null,
        // Browser engine follows the OS in the desktop app (WKWebView on
        // macOS, WebView2 on Windows), so this is what lets a rule name
        // Safari rather than infer it from `os_name` + `platform`.
        browser_name: browserName ?? null,

        // Desktop-specific Traits.
        // `desktop_version` and `cpu_arch` come from Redux state populated only
        // in Tauri builds, so they are null for web users.
        // `installer_type` / `distribution_channel` return the literal 'UNKNOWN'
        // (not null) for web users because their enums are closed — targeting
        // rules can match `!= 'UNKNOWN'` to scope to desktop users.
        desktop_version: desktopVersion ?? null,
        cpu_arch: cpuArch ?? null,
        // PRODA-174: host OS version/build for OS-scoped flag targeting and
        // staged rollouts (e.g. Windows 10 vs 11). null on web / pre-IPC.
        desktop_os_version: desktopOsVersion,
        installer_type: getDesktopInstallerType(),
        // APP-6116 (§5.11): deployment-inventory traits that double as policy
        // targeting (§5.18). `update_channel` is derived from the authenticated
        // region so it matches the channel the Rust updater binds; `build_id` is
        // the baked CI SHA (both null on web / local builds).
        update_channel: updateChannel,
        build_id: buildId,
        // distribution_channel is a build-time env value, available synchronously,
        // so seed it directly (not via the async atom): it lands on the first
        // setContext instead of forcing a second Flagsmith re-evaluation. Only
        // mdm_provider — the genuinely async, Windows-IPC-resolved dimension —
        // flips the atom and triggers one re-eval once detection completes.
        distribution_channel: getDistributionChannel(),

        // mdm_provider (APP-5958): emitted only once the flag-gated orchestrator
        // resolves it (desktop + flag on + Windows detection ran). The atom is
        // null on web / on-prem / flag off, so it's absent there — not a
        // misleading null. Matches the null convention of the other sinks.
        ...(installSourceContext
          ? { mdm_provider: installSourceContext.mdmProvider }
          : {}),

        // Integration / EHR Traits
        ehr_provider: ehrProvider,
        has_integration: hasIntegration,

        // Onboarding-session cohort traits. All three describe the SAME
        // IntegrationOnboardingSession document, so a targeting rule can AND
        // the provider with both timestamps without straddling sessions. Kept
        // separate from `ehr_provider` above, which can also resolve from a
        // generic EHR launch or the legacy EHR profile.
        integration_onboarding_provider: integrationOnboardingProvider ?? null,
        integration_onboarding_created_at: integrationOnboardingCreatedAt ?? null,
        integration_onboarding_updated_at: integrationOnboardingUpdatedAt ?? null,

        // Authentication Trait
        authentication_provider: authProvider ?? null,
      },
    };

    // Only update context if it has actually changed. The push goes through
    // the setGlobalFlagContext seam (P3 T2) — a verbatim pass-through, so
    // this file keeps sole ownership of the context SHAPE (targetingKey +
    // nested `traits`, the Flagsmith provider's convention).
    if (!isEqual(previousContextRef.current, context)) {
      void setGlobalFlagContext(context);
      previousContextRef.current = context;
    }
  }, [
    userData?.user_ref_id,
    userData?.email,
    userData?.country,
    userData?.first_name,
    userData?.last_name,
    userData?.phone_number,
    userData?.tier,
    userData?.kinde_user_id,
    userData?.user_mfa_info?.is_mfa_enabled,
    userData?.session_settings?.opted_in_experimental,
    userData?.language_time_settings?.display_language,
    userData?.is_managed,
    teamInfo.teamOrgId,
    teamInfo.teamDetails?.team.name,
    teamInfo.userRoleInTeam,
    teamInfo.teamDetails?.team.tier,
    teamInfo.teamDetails?.team.mfa_info?.method,
    currentMember?.tier,
    currentMember?.created_at,
    region,
    updateChannel,
    buildId,
    joinedOrg?.org_id,
    joinedOrg?.org_name,
    workspaceId,
    billingInfo?.third_party_license?.latest_tier,
    billingInfo?.tier,
    desktopVersion,
    cpuArch,
    desktopOsVersion,
    ehrProvider,
    hasIntegration,
    integrationOnboardingProvider,
    integrationOnboardingCreatedAt,
    integrationOnboardingUpdatedAt,
    authProvider,
    installSourceContext,
    isSettledAnonymous,
  ]);

  useEffect(() => {
    if (!isSettledAnonymous || userData?.user_ref_id) return;
    if (isEqual(previousContextRef.current, {})) return;
    previousContextRef.current = {};
    void setGlobalFlagContext({});
  }, [isSettledAnonymous, userData?.user_ref_id]);

  // APP-6116: keep the Snowplow client_context + Datadog global-context
  // `update_channel` in sync as the authenticated region resolves. This
  // component subscribes to useQueryKindeProperties, so — unlike the identify
  // effect, which can fire before the Kinde query resolves and would then strand
  // them at `update_channel: null` for the whole session — this re-attaches the
  // channel the moment it becomes known. The Datadog global context also makes
  // auto-collected RUM errors / native crashes (which don't go through
  // trackEventV2's per-event props) filterable by update_channel.
  useEffect(() => {
    setSnowplowClientContext({ update_channel: updateChannel });
    setDatadogUpdateChannel(updateChannel);
  }, [updateChannel]);

  // Re-attach once the Kinde query resolves, like `update_channel` above.
  useEffect(() => {
    setDatadogRegion(authenticatedRegion);
  }, [authenticatedRegion]);

  // The AUTHENTICATED region is the only region Datadog records
  // (`setDatadogRegion` above, sourced from `kindeProperties.region` per C10).
  // Nothing recorded the region that actually PICKED THE API HOST, so the two
  // were never comparable and a claim resolving to the wrong stack was
  // invisible in prod — which is why this defect needed a UK clinician to hit
  // it before anyone saw it.
  //
  // One session-scoped observation closes that: emit once when the resolved
  // region and the authenticated region disagree. A disagreement means the
  // caller is addressing a different regional backend than the one their
  // account is registered in — a claim still resolving wrongly.
  //
  // What it does NOT cover, so nobody reads it as broader coverage than it
  // has: the second, independent fail-open at `create-api-config.ts`
  // (`scribeDomains[region] ?? AU`). That one swallows a CORRECTLY-resolved
  // region whose key is absent from the environment's domain map, and this
  // effect never observes that lookup — the two regions compare equal and
  // nothing is emitted while the request still goes to AU. Closing it needs a
  // signal at the lookup itself, and is recorded on the ticket.
  //
  // Both fields are canonical `Region` enum values and the third is a boolean:
  // the raw claim is NEVER logged. `?region=` is user-controllable and
  // unbounded, the class `.ai-context/telemetry-phi-pii-safety.md` says to log
  // shape for and never value. Deliberately a log line rather than a product
  // event or a new global dimension — it needs no Iglu schema and no
  // `GLOBAL_DIMENSIONS` row, and answers the question where it is asked.
  //
  // Both values are re-normalised at the boundary rather than trusted for
  // being typed `Region`. The type is not a runtime guarantee here: several
  // slots in `useRegion()`'s chain are populated by a cast rather than a
  // check, the widest being `widgetRegionAtom` — the widget-auth launch reads
  // `?region=` straight off the URL, casts it, and persists it, so
  // `useRegion()` can return an arbitrary caller-supplied string from a slot
  // sitting ABOVE `userRegion`. Logging it unchecked would send exactly the
  // unbounded user-controlled value this comment promises never to emit.
  // A value that does not normalise is reported as the bounded sentinel
  // below, which is also the sharper signal: the resolved region is not a
  // region at all.
  const regionDivergenceLoggedRef = useRef(false);
  useEffect(() => {
    const canonicalAuthenticatedRegion = toValidRegion(authenticatedRegion);
    const canonicalResolvedRegion = toValidRegion(region);

    if (!canonicalAuthenticatedRegion || regionDivergenceLoggedRef.current) return;
    if (canonicalAuthenticatedRegion === canonicalResolvedRegion) return;

    regionDivergenceLoggedRef.current = true;
    logger.warn('[region] resolved region differs from authenticated region', {
      authenticatedRegion: canonicalAuthenticatedRegion,
      resolvedRegion: canonicalResolvedRegion ?? NON_CANONICAL_REGION,
      regionsMatch: false,
    });
  }, [authenticatedRegion, region]);

  useEffect(() => {
    const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);

    const syncFlags = () => {
      try {
        const provider = OpenFeature.getProvider(FEATURE_FLAG_DOMAIN) as Partial<
          FlagsmithClientProvider & {
            flagsmithClient?: {
              getAllFlags?: () => Record<string, { enabled: boolean; value?: unknown }>;
            };
          }
        >;
        const flags = provider.flagsmithClient?.getAllFlags?.();
        if (flags) setFlagsmithFlagUserProperties(flags);
      } catch {
        // Provider not ready or getAllFlags unavailable — non-fatal.
      }
    };

    client.addHandler(ProviderEvents.Ready, syncFlags);
    client.addHandler(ProviderEvents.ConfigurationChanged, syncFlags);

    return () => {
      client.removeHandler(ProviderEvents.Ready, syncFlags);
      client.removeHandler(ProviderEvents.ConfigurationChanged, syncFlags);
    };
  }, []);

  return null;
}
